import apolloClient from 'api/apolloClient';
import { makeAutoObservable, runInAction } from 'mobx';
import { EnrollmentPeriod, EnrollmentStage, FinalBrowsingCriteria, Proposal } from 'types/enrollmentService';
import {
  BUILD_ENROLLMENT,
  DELETE_PROPOSAL,
  GET_ALL_PROPOSALS_FROM_ALL_ENROLLMENT_PERIODS_BY_GROUP_ID,
  GET_ENROLLMENT_PERIOD_BY_ID,
} from 'api/queries/enrollmentService';
import { INITIAL_ENROLLMENT_OBJECT } from 'store/modules/enrollment/enrollmentPeriodModule';

export const DEFAULT_DASHBOARD_ENROLLMENT_OBJECT = Object.freeze({
  id: '',
  effectiveDate: '',
  productLineIds: [],
  stage: EnrollmentStage.NEW_LEAD,
  proposals: [],
});

export const DEFAULT_RENEWAL_ENROLLMENT_OBJECT = Object.freeze({
  id: '',
  groupId: '',
  effectiveDate: '',
  productLineIds: [],
  stage: EnrollmentStage.RENEWAL,
});

/**
 * @DEPRECATED Use enrollment module instead, or simply /operations if you need to query data
 * without direct EnrollmentPeriod object management.
 */
export class EnrollmentStore {
  // Data
  enrollmentPeriod: EnrollmentPeriod = INITIAL_ENROLLMENT_OBJECT;
  enrollmentPeriodsByGroupId: EnrollmentPeriod[] = [];

  public setEnrollmentPeriods(periods: EnrollmentPeriod[]): void {
    this.enrollmentPeriodsByGroupId = periods;
  }

  async getEnrollmentPeriod(periodId: string): Promise<void> {
    const res = await apolloClient.query<{ getEnrollmentPeriod: EnrollmentPeriod }>({
      query: GET_ENROLLMENT_PERIOD_BY_ID,
      fetchPolicy: 'no-cache',
      variables: { periodId },
    });
    runInAction(() => {
      if (res.data) {
        this.enrollmentPeriod = res.data.getEnrollmentPeriod;
      }
    });
  }

  /**
   * @Derecated, use getAllEnrollmentPeriodsByGroupId instead
   * Method saves to store enrollment periods that Active (NEW SALE, or RENEWAL) and skipped inactive (Archived)
   * @param groupId
   */
  async getAllActiveEnrollmentPeriodsByGroupId(groupId: string): Promise<void> {
    const res = await apolloClient.query<{ getAllEnrollmentPeriodsByGroupId: EnrollmentPeriod[] }>({
      query: GET_ALL_PROPOSALS_FROM_ALL_ENROLLMENT_PERIODS_BY_GROUP_ID,
      fetchPolicy: 'no-cache',
      variables: { groupId, showInstalled: true },
    });
    runInAction(() => {
      if (res.data) {
        this.setEnrollmentPeriods(res.data.getAllEnrollmentPeriodsByGroupId);
      }
    });
  }

  async deleteProposal(periodId: string, cartId: string): Promise<void> {
    const res = await apolloClient.mutate<{ getEnrollmentPeriod: EnrollmentPeriod }>({
      mutation: DELETE_PROPOSAL,
      variables: { periodId, cartId },
    });
    runInAction(() => {
      if (res.data) {
        this.enrollmentPeriod = res.data.getEnrollmentPeriod;
      }
    });
  }

  async completeEnrollment(
    periodId: string,
    submittedByUserName: string,
    finalBC: FinalBrowsingCriteria,
  ): Promise<void> {
    const res = await apolloClient.mutate<{ completeEnrollment: EnrollmentPeriod }>({
      mutation: BUILD_ENROLLMENT,
      variables: { periodId, submittedByUserName, finalBC },
    });
    runInAction(() => {
      if (res.data) {
        this.enrollmentPeriod = res.data.completeEnrollment;
      }
    });
  }

  public getProposalById(cartId: string): Proposal {
    const proposal = this.enrollmentPeriod.proposals.find((p) => p.cartId === cartId);
    if (!proposal) {
      throw new Error(`No proposal with id: ${cartId}`);
    }
    return proposal;
  }

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }
}
